/**
 * The money variant's two interactive bands, and nothing else.
 *
 * `video-money-math` and `video-leak-check` both render their figures server-side for the values
 * they start at, so neither band is an empty box before this file runs or with JavaScript off:
 * the calculator reads as a finished total and the checklist reads as a form. This script only
 * moves the numbers once the visitor changes something.
 *
 * Both bands are sections, and both re-run when the theme editor swaps one in, so every init is
 * written to be safe on a second pass: it finds its root by `data-` attribute and hands back early
 * when the root is not on the page.
 */

/** The visitor's motion preference, read once and re-read on change so a mid-session flip lands. */
let vmReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (event) => {
  vmReduced = event.matches;
});

const VM_COUNT_MS = 1500;

/**
 * Money in the shop's own currency and locale, with no cents: every figure on these bands is a
 * long-run estimate, and "$244,015.37" claims a precision the arithmetic does not have.
 *
 * The currency comes from Liquid's `cart.currency.iso_code` and the locale from
 * `request.locale.iso_code`, both read off the band, because the storefront has no reliable
 * client-side source for either. A currency `Intl` rejects falls back to grouping the number and
 * prefixing the code rather than throwing inside a render pass.
 *
 * @param {Element} root - the band, carrying `data-vl-locale` and `data-vl-currency`.
 * @returns {(value: number) => string}
 */
function vmMoneyFormatter(root) {
  const locale = root.getAttribute('data-vl-locale') || 'en';
  const currency = (root.getAttribute('data-vl-currency') || 'USD').toUpperCase();

  try {
    const format = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
    // Construction is the validation: an unsupported currency code throws there, not on format().
    return (value) => format.format(Math.round(value));
  } catch (error) {
    const grouping = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return (value) => `${currency} ${grouping.format(Math.round(value))}`;
  }
}

/**
 * @param {Element} root
 * @returns {{rate: number, years: number, delay: number}}
 */
function vmMathConfig(root) {
  return {
    rate: parseFloat(root.getAttribute('data-vl-rate')) || 0,
    years: parseInt(root.getAttribute('data-vl-years'), 10) || 0,
    // `delay-years` is the setting; `data-vl-delay` is the figure this band prints, and the two
    // sharing a name once made every `[data-vl-delay]` query ambiguous.
    delay: parseInt(root.getAttribute('data-vl-delay-years'), 10) || 0,
  };
}

/**
 * The whole calculator, from one monthly figure. Mirrors the arithmetic the band pre-renders in
 * Liquid so the first paint and every later paint agree.
 *
 * @param {number} monthly
 * @param {{rate: number, years: number, delay: number}} config
 * @returns {{total: number, principal: number, growth: number, delayLoss: number, perMonth: number}}
 */
function vmProject(monthly, config) {
  const monthlyRate = config.rate / 100 / 12;
  const months = config.years * 12;

  /** Future value of `count` monthly deposits, the same series Liquid sums server-side. */
  const futureValue = (count) => {
    if (monthlyRate <= 0) return monthly * count;
    return (monthly * (Math.pow(1 + monthlyRate, count) - 1)) / monthlyRate;
  };

  const total = futureValue(months);
  const principal = monthly * months;
  const delayMonths = Math.max(months - config.delay * 12, 0);
  const perMonth = months >= 12 ? (total - futureValue(months - 12)) / 12 : 0;

  return {
    total,
    principal,
    growth: total - principal,
    delayLoss: Math.max(total - futureValue(delayMonths), 0),
    perMonth,
  };
}

/** Keep the final CTA's cross-section urgency figure in step with the calculator. */
function vmPaintFinal(format, value) {
  document.querySelectorAll('[data-vl-final-month]').forEach((target) => {
    target.textContent = format(value);
  });
}

/**
 * The signature band: the slider, the 30-year total, the split bar and the two cost-of-waiting
 * figures.
 */
function initMoneyMath() {
  document.querySelectorAll('[data-vl-math]').forEach((root) => {
    // The theme editor replaces a section's markup, so a root that is already wired belongs to the
    // pass before this one; a fresh node never carries the flag.
    if (root.dataset.vlWired) {
      const existingSlider = root.querySelector('[data-vl-amount-input]');
      if (!existingSlider) return;
      const monthly = parseFloat(existingSlider.value) || 0;
      vmPaintFinal(vmMoneyFormatter(root), vmProject(monthly, vmMathConfig(root)).perMonth);
      return;
    }
    root.dataset.vlWired = 'true';

    const slider = root.querySelector('[data-vl-amount-input]');
    const amountLabel = root.querySelector('[data-vl-amount]');
    const totalLabel = root.querySelector('[data-vl-total]');
    if (!slider || !totalLabel) return;

    const format = vmMoneyFormatter(root);
    const config = vmMathConfig(root);
    const barYou = root.querySelector('[data-vl-bar-you]');
    const barGrowth = root.querySelector('[data-vl-bar-growth]');

    /** Every output except the headline total, which the count-up owns on the way up. */
    const paint = (values, monthly) => {
      if (amountLabel) amountLabel.textContent = format(monthly);
      totalLabel.textContent = format(values.total);
      const principal = root.querySelector('[data-vl-principal]');
      const growth = root.querySelector('[data-vl-growth]');
      if (principal) principal.textContent = format(values.principal);
      if (growth) growth.textContent = format(values.growth);

      if (barYou && values.total > 0) {
        barYou.style.width = `${(values.principal / values.total) * 100}%`;
      }
      if (barGrowth && values.total > 0) {
        barGrowth.style.width = `${(values.growth / values.total) * 100}%`;
      }

      const delayLoss = root.querySelector('[data-vl-delay]');
      const perMonth = root.querySelector('[data-vl-per-month]');
      if (delayLoss) delayLoss.textContent = format(values.delayLoss);
      if (perMonth) perMonth.textContent = format(values.perMonth);
      vmPaintFinal(format, values.perMonth);
    };

    /**
     * @param {boolean} animate
     */
    const update = (animate) => {
      const monthly = parseFloat(slider.value) || 0;
      const values = vmProject(monthly, config);

      if (!animate || vmReduced || values.total <= 0) {
        paint(values, monthly);
        return;
      }

      // A single orchestrated moment on first view, the same one the design counts up: the
      // total runs from zero and the rest of the figures land when it stops.
      const started = performance.now();
      const step = (now) => {
        const progress = Math.min((now - started) / VM_COUNT_MS, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        totalLabel.textContent = format(values.total * eased);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          paint(values, monthly);
        }
      };
      paint({ ...values, total: 0 }, monthly);
      requestAnimationFrame(step);
    };

    slider.addEventListener('input', () => update(false));

    // The final CTA lives in another section and has no Liquid access to this section's calculator
    // settings. Seed it immediately, then keep it in step through `paint` on every slider change.
    const initialMonthly = parseFloat(slider.value) || 0;
    const initialValues = vmProject(initialMonthly, config);
    vmPaintFinal(format, initialValues.perMonth);

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.disconnect();
          update(true);
        });
      },
      { threshold: 0.5 }
    );
    observer.observe(root);
  });
}

/**
 * The self-check band: four yes/no rows, an annual leak estimate, and the named leaks behind it.
 *
 * Rows are radio pairs, so the group is exclusive for free, arrow keys move within a row, and a
 * screen reader announces the row's question as the group's name. Nothing is hidden from the
 * server: every row and both outcome sentences are in the markup, and this only picks which
 * sentence is showing.
 */
function initLeakCheck() {
  document.querySelectorAll('[data-vl-leak]').forEach((root) => {
    if (root.dataset.vlWired) return;
    root.dataset.vlWired = 'true';

    const inputs = Array.from(root.querySelectorAll('input[type="radio"]'));
    const number = root.querySelector('[data-vl-leak-num]');
    // The class goes on the element the stylesheet names (`.vl-leak__num.is-zero`), not on the
    // inner span: toggled there it matched no rule and the all-clear stayed red.
    const numberLine = root.querySelector('.vl-leak__num');
    const label = root.querySelector('[data-vl-leak-label]');
    if (!inputs.length || !number || !label) return;

    const format = vmMoneyFormatter(root);
    const prefix = root.querySelector('[data-vl-leak-pre]');
    const suffix = root.querySelector('[data-vl-leak-suf]');
    const fixesTarget = root.querySelector('[data-vl-leak-fixes]');
    const leakingFix = root.querySelector('[data-vl-fix-leaking]');
    const clearFix = root.querySelector('[data-vl-fix-clear]');
    const labelWaiting = root.getAttribute('data-vl-label-waiting') || '';
    const labelLosing = root.getAttribute('data-vl-label-losing') || '';
    const labelClear = root.getAttribute('data-vl-label-clear') || '';
    const labelNone = root.getAttribute('data-vl-label-none') || '';
    const rows = inputs.length / 2;

    // The `~` and the trailing "a year" are markup, not strings built here: they carry the
    // merchant's own wording and its non-breaking space, and this only decides whether they show.
    const showAffixes = (visible) => {
      if (prefix) prefix.hidden = !visible;
      if (suffix) suffix.hidden = !visible;
    };

    const markClear = (clear) => {
      if (numberLine) numberLine.classList.toggle('is-zero', clear);
    };

    const render = () => {
      const answered = inputs.filter((input) => input.checked).length;

      if (!answered) {
        number.textContent = '—';
        label.textContent = labelWaiting;
        markClear(false);
        showAffixes(false);
        if (leakingFix) leakingFix.hidden = true;
        if (clearFix) clearFix.hidden = true;
        return;
      }

      const leaks = inputs
        .filter((input) => input.checked && input.value === '1')
        .map((input) => ({
          cost: parseFloat(input.getAttribute('data-cost')) || 0,
          fix: input.getAttribute('data-fix') || '',
        }));
      const total = leaks.reduce((sum, leak) => sum + leak.cost, 0);

      if (total <= 0) {
        number.textContent = format(0);
        label.textContent = answered < rows ? labelClear : labelNone;
        markClear(true);
        showAffixes(false);
        if (leakingFix) leakingFix.hidden = true;
        if (clearFix) clearFix.hidden = false;
        return;
      }

      number.textContent = format(total);
      label.textContent = labelLosing;
      markClear(false);
      showAffixes(true);
      if (fixesTarget) {
        fixesTarget.textContent = leaks
          .map((leak) => leak.fix)
          .filter(Boolean)
          .join(', ');
      }
      if (leakingFix) leakingFix.hidden = false;
      if (clearFix) clearFix.hidden = true;
    };

    inputs.forEach((input) => input.addEventListener('change', render));
  });
}

function initVideoMoney() {
  initMoneyMath();
  initLeakCheck();
}

initVideoMoney();

// Re-run when the theme editor swaps a section in.
document.addEventListener('shopify:section:load', initVideoMoney);
